/*
 * Image/Document orders for a transaction:
 * 
 * Front Verification (1 request):
 * - `document_front`
 * 
 * Front & Back Verification (2 requests):
 * - `document_front`
 * - `document_back`
 * 
 * Front & Back & Selfie (Basic) Verification (3 requests):
 * - `document_front`
 * - `document_back`
 * - `selfie_front`
 * 
 * Front & Back & Selfie (Liveness) Verification (3 requests):
 * - `document_front`
 * - `document_back`
 * - `selfie_front`
 * - `selfie_left`
 * - `selfie_right`
 */

import { IMAGE_SUBJECTS, STEPS } from "@/api/constants";

/**
 * Returns an array of image subject(s) required for the
 * Upload Image API request based on the current step.
 * */
export const getImageSubject = (step: STEPS): IMAGE_SUBJECTS[] => {
  switch (step) {
    case STEPS.UPLOAD_DOCUMENT_FRONT:
      return [IMAGE_SUBJECTS.DOCUMENT_FRONT];
    case STEPS.UPLOAD_DOCUMENT_BACK:
      return [IMAGE_SUBJECTS.DOCUMENT_BACK];
    case STEPS.UPLOAD_SELFIE_BASIC:
      return [IMAGE_SUBJECTS.SELFIE_FRONT];
    case STEPS.UPLOAD_SELFIE_LIVENESS:
      return [
        IMAGE_SUBJECTS.SELFIE_FRONT,
        IMAGE_SUBJECTS.SELFIE_LEFT,
        IMAGE_SUBJECTS.SELFIE_RIGHT,
      ];
    default:
      throw new Error(`Unhandled step: ${step}`);
  }
}

export interface IFormattedDocument {
  data: string;
  format: string;
  url?: string;
  image_subject: string;
  supplementary_data?: {
    extracted_barcode: {
      barcode_type: string;
      extracted_data: string;
    }
  }
}

export class Document {
  /** image format */
  _format: string = "";
  /** A list of image tuples with the shape `[<image-format>, <image-data>]` */
  _images: string[][] = [];
  _barcode: string = "";

  constructor({ images }: { images: string[] }) {
    this.setImages(images);
  }

  setImages(images: string[]) {
    /** 
     * Returns a list of tuples with image format and image data
     * 
     * Ex. `["image/jpg", <base64-encoded-image-data>]`
     * */
    const imgTuples = images.map((image) => {
      const tuple = image.replace("data:", "").split(";base64,");
      /* image format should be consistent and remain the same for all photos taken */
      this._format = tuple[0] || "image/jpeg";
      return tuple;
    });
    this._images = imgTuples;
  }

  /**
   * Returns the image tuple indexed by the `pos` param. If `pos` is
   * undefined, return the first image tuple in the list.
   * */
  getImage(pos?: number): string[] {
    if (!pos) {
      return this._images[0] || [];
    }
    return this._images[pos] || [];
  }

  /** Returns the list of image tuples */
  getAllImages() {
    return this._images;
  }

  // set image(image: string) {
  //     const [format, data] = image.replace("data:", "").split(";base64,");
  //     this._format = format || "";
  //     this._image = data || "";
  // }

  get barcode() {
    return this._barcode;
  }

  set barcode(data: string) {
    this._barcode = data || "";
  }

  formatPayload(step: STEPS | ""): IFormattedDocument[] {
    switch (step) {
      case STEPS.UPLOAD_DOCUMENT_BACK:
        return this.back();
      case STEPS.UPLOAD_SELFIE_BASIC:
        return this.selfieFront();
      case STEPS.UPLOAD_SELFIE_LIVENESS:
        return this.selfieLiveness();
      case STEPS.UPLOAD_DOCUMENT_FRONT:
      default:
        return this.front();
    }
  }

  /** Front of Driver's License */
  front(): IFormattedDocument[] {
    return [{
      data: this.getImage()[1] || "",
      format: this._format,
      image_subject: IMAGE_SUBJECTS.DOCUMENT_FRONT,
    }];
  }

  /** Back of Driver's License */
  back(): IFormattedDocument[] {
    if (!this.barcode) {
      return [{
        data: this.getImage()[1] || "",
        format: this._format,
        image_subject: IMAGE_SUBJECTS.DOCUMENT_BACK,
      }];
    }

    return [{
      data: this.getImage()[1] || "",
      format: this._format,
      image_subject: IMAGE_SUBJECTS.DOCUMENT_BACK,
      supplementary_data: {
        extracted_barcode: {
          barcode_type: "pdf417",
          extracted_data: this.barcode,
        }
      },
    }];
  }

  selfieFront(): IFormattedDocument[] {
    return [{
      data: this.getImage()[1] || "",
      format: this._format,
      image_subject: IMAGE_SUBJECTS.SELFIE_FRONT,
    }];
  }

  selfieLiveness(): IFormattedDocument[] {
    return [
      {
        data: this.getImage(0)[1] || "",
        format: this._format,
        image_subject: IMAGE_SUBJECTS.SELFIE_FRONT,
      },
      {
        data: this.getImage(1)[1] || "",
        format: this._format,
        image_subject: IMAGE_SUBJECTS.SELFIE_LEFT,
      },
      {
        data: this.getImage(2)[1] || "",
        format: this._format,
        image_subject: IMAGE_SUBJECTS.SELFIE_RIGHT,
      },
    ];
  }
}